import {startCase} from 'lodash';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {Plugins, Component, Expressions} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {AssetState as AssetStateType} from '@packages/systems/designer/assets';
import {getObject, getString} from '@packages/utilities/json';
import {LOTTIE_PLACEHOLDER} from '@packages/systems/core/utils/LottieUtils';

import {
  isComponentInst,
  isUserComponentInst,
  CHILDREN_PATH,
} from '@packages/utilities/core-elements';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';
import {
  isComponentOnlyDropTarget,
  isDOM,
  isImageElement,
  isLinkElement,
  isListElement,
  isDynamicList,
  isRive,
  isSlot,
  isTypographic,
  isLottie,
  isHeading,
} from '@packages/utilities/elementTypeUtils';
import {isComponentInsidePageSlot} from './isComponentInsidePageSlot';
import {
  getElementLabel,
  getSlotName,
  isChildrenBound,
  getElementAssetId,
} from '.';
import {getStringFromListOfElements} from '@packages/utilities/element-content';

/**
 * Due to corrupt data style block ids occasionally exist on elements & nodes that no longer exist.
 * This function keeps looking for the first _valid_ style block that has a name and returns that.
 *
 * Element styles (type: 'element') are explicitly excluded because they should not be used for
 * display names in the navigator or on-canvas controls.
 *
 * The long term solution is to identify what is causing this invalid state, fixing it, and then
 * writing a migration to fix corrupt data.
 * See: https://app.clickup.com/t/2310520/BUG-450
 */
function getFirstValidNameFromStyleBlocks(
  ids: Array<string>,
  styleBlockState: StyleBlockStateType
): string | null {
  for (const id of ids) {
    const block = styleBlockState.getBlock(id);

    // Skip element styles - they should not be used for display names
    if (block?.type === 'element') continue;

    const name = block?.get('name');
    if (name) return name;
  }
  return null;
}

function getFirstStyleBlockName(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  styleBlockState: StyleBlockStateType
): string | null {
  // Only get style block names for non-component instances
  if (isComponentInst(componentMap)(element)) return null;

  const ids = getStyleBlockIds(element);
  return getFirstValidNameFromStyleBlocks(ids, styleBlockState);
}

/**
 * Returns the style block name for an element, respecting page slot restrictions.
 * Page slots (component-only drop targets) should not show class names.
 */
function getNameFromStyleBlock(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  styleBlockState: StyleBlockStateType
): string | null {
  // Page slots should not show class names
  const isPageSlot = isComponentOnlyDropTarget(element);
  if (isPageSlot) return null;

  return getFirstStyleBlockName(element, componentMap, styleBlockState);
}

const LOTTIE_PLACEHOLDER_ID = LOTTIE_PLACEHOLDER.getPlaceholderData('').id;
const RIVE_PLACEHOLDER_ID = 'plugins/Animation/assets/wf-rive-placeholder.riv';
const IMAGE_PLACEHOLDER_ID = 'plugins/Basic/assets/placeholder.svg';

/**
 * Truncates a string to 40 characters maximum, adding an ellipsis if truncation occurs.
 * Trims any trailing whitespace from the substring before adding the ellipsis.
 */
function truncateWithEllipsis(str: string): string {
  const trimmed = str.trimEnd();
  const charLimit = 40;
  if (trimmed.length <= charLimit) {
    return trimmed;
  }
  return trimmed.substring(0, charLimit).trimEnd() + '...';
}
/**
 * Returns the asset name from an element if it is an image element.
 * Returns null if the element is not an image element or if the asset id is the placeholder id.
 */
const getAssetNameFromElement = (
  element: ExpressionElement,
  assetState: AssetStateType
): string | null => {
  if (isImageElement(element)) {
    const assetId = getElementAssetId(element);
    if (!assetId || assetId === IMAGE_PLACEHOLDER_ID) return null;

    const asset = assetState.getById(assetId);
    if (asset?.origFileName === 'placeholder.svg') return null;
    return asset?.origFileName ?? null;
  }

  if (isLinkElement(element)) {
    const linkExpr = Expressions.getIn(Expressions.getElementData(element), [
      {in: 'Record', at: 'link'},
    ]);
    if (!linkExpr) return null;

    const json = Expressions.getLiteral(linkExpr);
    if (!json) return null;

    const jsonObject = getObject(json.value);
    if (!jsonObject?.assetId) return null;

    const assetId = getString(jsonObject.assetId);
    if (!assetId) return null;

    const asset = assetState.getById(assetId);
    return asset?.origFileName ?? null;
  }

  if (isLottie(element)) {
    const lottieExpr = Expressions.getIn(Expressions.getElementData(element), [
      {in: 'Record', at: 'animation'},
      {in: 'Union', tag: 'lottie'},
      {in: 'Record', at: 'path'},
    ]);
    if (!lottieExpr) return null;

    const json = Expressions.getLiteral(lottieExpr);
    if (!json) return null;

    const jsonObject = getObject(json.value);
    if (jsonObject?.id === LOTTIE_PLACEHOLDER_ID || !jsonObject?.origFileName) {
      return null;
    }

    const origFileName = getString(jsonObject?.origFileName);
    return origFileName ?? null;
  }

  if (isRive(element)) {
    const riveExpr = Expressions.getIn(Expressions.getElementData(element), [
      {in: 'Record', at: 'animation'},
      {in: 'Record', at: 'path'},
    ]);
    if (!riveExpr) return null;

    const json = Expressions.getLiteral(riveExpr);
    if (!json) return null;

    const jsonObject = getObject(json.value);
    if (!jsonObject?.origFileName) return null;

    const origFileName = getString(jsonObject?.origFileName);
    if (jsonObject?.id === RIVE_PLACEHOLDER_ID || !origFileName) {
      return null;
    }

    return origFileName;
  }

  return null;
};

/**
 * Returns the text from the children of an element if it is typographic and children are not bound.
 * This will get text only from direct children, not grandchildren or other descendants.
 * Otherwise, returns null.
 *
 * The return string is capped at 40 characters.
 */
function getStringFromChildren(element: ExpressionElement): string | null {
  if (!isTypographic(element) || isChildrenBound(element)) return null;

  const data = Expressions.getElementData(element);
  const children = Expressions.getIn(data, CHILDREN_PATH);
  if (!children) return null;

  const str = getStringFromListOfElements(children);
  if (str.length === 0) return null;
  // if the element is a heading and the text is just 'Heading', fall back to the tag name
  if (isHeading(element) && str.trim() === 'Heading') return null;

  // Remove zero-width spaces, zero-width joiners, and other whitespace characters
  // Note this isn't the full range of all potential invisible unicode characters,
  // but should cast a reasonably large net to detect those which would be commonly used
  const visibleStr = str.replace(/^[\s\u200B-\u200F\u2060-\u206F\uFEFF]+/g, '');

  // If after removing invisible characters there's nothing left, return null
  if (visibleStr.length === 0) return null;

  return truncateWithEllipsis(visibleStr);
}

/**
 * Human-readable label for a bare HTML tag name, aligned with tag-based defaults in
 * {@link getNameFromElementTag} (Paragraph, Heading 1–6, Navigation, title case fallback).
 * Use when there is no `ExpressionElement` (e.g. code component shadow DOM selection).
 *
 * Pass `{codeComponentShadowSelection: true}` from the VCC style header so non-special tags
 * stay literal lowercase (`path`, `span`); omit everywhere else (navigator uses `startCase`).
 */
export function getDisplayLabelForHtmlTagName(
  tagName: string,
  options?: {codeComponentShadowSelection?: boolean}
): string {
  const normalizedTag = tagName.toLowerCase().trim();
  if (!normalizedTag) return 'Element';

  if (normalizedTag === 'nav') return 'Navigation';
  if (normalizedTag === 'p') return 'Paragraph';

  const headingMatch = normalizedTag.match(/^h([1-6])$/);
  if (headingMatch) return `Heading ${headingMatch[1]}`;

  if (options?.codeComponentShadowSelection) {
    return normalizedTag.includes('-')
      ? startCase(normalizedTag)
      : normalizedTag;
  }

  return startCase(normalizedTag);
}

/**
 * Derives a human-readable name for an element based on its selected or provided tag.
 *
 * Priority order for default names:
 * 1. For div tags: fallback to the element name
 * 2. For specific tags: use human-readable versions
 *    - h1-h6 → Heading 1-6
 *    - nav → Navigation
 *    - p → Paragraph
 * 3. For all other tags: convert to title case (e.g., aside → Aside, hello-world → Hello World)
 *
 * Note: This function is not applied to Page Slot elements or List elements.
 */
function getNameFromElementTag(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): string | null {
  if (isComponentInsidePageSlot(componentMap, element)) return null;
  if (isListElement(element) || isDynamicList(element)) return null;

  const data = Expressions.getElementData(element);
  const fields = Expressions.getRecord(data);
  const tagExpr = fields?.tag;
  if (!tagExpr) return null;

  // Tags can be an enum if selected or text if it's provided to a custom element
  const tag = Expressions.getEnum(tagExpr) || Expressions.getText(tagExpr);

  // When the tag value is dynamic (i.e. `tag` is undefined) and the element
  // itself is a heading, treat it as a generic Heading label.
  if (!tag) return isHeading(element) ? 'Heading' : null;

  const normalizedTag = tag.toLowerCase();

  // List-related elements should not have default names
  if (['li', 'ul', 'ol'].includes(normalizedTag)) {
    return null;
  }

  // Special cases for specific tags
  if (normalizedTag === 'div') {
    // the DOM element's has a different default name we want to use
    return isDOM(element) ? 'Custom Element' : null;
  }

  return getDisplayLabelForHtmlTagName(normalizedTag);
}

/**
 * Returns the explicit display name from element meta data if it exists and is non-empty.
 * This is the highest priority customization.
 */
function getDisplayNameFromMeta(element: ExpressionElement): string | null {
  const displayName = element.meta?.displayName;
  if (typeof displayName === 'string' && displayName !== '') {
    return displayName;
  }
  return null;
}

/**
 * Returns the component display name if the element is a user component instance.
 * Component instances always use the component name as the default.
 */
function getComponentInstanceName(
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): string | null {
  if (!isUserComponentInst(componentMap, element)) return null;

  const component = componentMap.get(element.type);
  return component ? Component.getDisplayName(component) : null;
}

/**
 * Returns the slot name for slot elements if available.
 * TODO: remove this once we've migrated all slot elements to use
 * element.meta.displayName instead of element.data.name
 */
function getSlotElementName(element: ExpressionElement): string | null {
  if (!isSlot(element)) return null;
  return getSlotName(element);
}

const memo_getUserCustomizedElementLabel = weakMemo(
  (element: ExpressionElement) => {
    // Priority 1: Use explicit display name from element meta data
    const displayName = getDisplayNameFromMeta(element);
    if (displayName) return displayName;

    return null;
  }
);

const memo_getDefaultElementLabel = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((styleBlockState: StyleBlockStateType) =>
    weakMemo((assetState: AssetStateType) =>
      weakMemo((componentMap: Component.ComponentMap) =>
        weakMemo((currentComponentRender: Expression) =>
          weakMemo((element: ExpressionElement) => {
            // Priority 1: Component instances always use the component name as the default
            const componentInstanceName = getComponentInstanceName(
              element,
              componentMap
            );
            if (componentInstanceName) return componentInstanceName;

            // Priority 2: For slot elements, use the slot name if available
            // TODO: remove this once we've migrated all slot elements to use
            // element.meta.displayName instead of element.data.name
            const slotElementName = getSlotElementName(element);
            if (slotElementName) return slotElementName;

            // Priority 3: Use the first style block name if available
            const firstClassName = getNameFromStyleBlock(
              element,
              componentMap,
              styleBlockState
            );
            if (firstClassName) return firstClassName;

            // Priority 4: Use the text from the element if it is typographic
            const text = getStringFromChildren(element);
            if (text) return text;

            // Priority 5: Use the asset name if available
            const assetName = getAssetNameFromElement(element, assetState);
            if (assetName) return assetName;

            // Priority 6: fallback to the node's HTML tag or block type
            const tagName = getNameFromElementTag(element, componentMap);
            if (tagName) return tagName;

            // Priority 7: Use the label directly from the element
            const elementLabel = getElementLabel(
              element,
              currentComponentRender,
              componentMap,
              plugins
            );

            return elementLabel;
          })
        )
      )
    )
  )
);

/**
 * Returns a customized label for a given element if it is present. If not -
 * uses a priority list to calculate default label (see getDefaultElementLabel for more details)
 *
 * @param element - The expression element to get the label for
 * @param currentComponentRender - The current component render expression
 * @param componentMap - Map of components for component type checking
 * @param plugins - Plugin system for the WFDL renderer
 * @param styleBlockState - Style block state for retrieving style information
 * @param assetState - Asset state for retrieving asset information
 * @returns A string label representing the customized element name (if defined)
 *  otherwise returns default label
 */
export function getUserCustomizedElementLabel(
  element: ExpressionElement,
  currentComponentRender: Expression,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  styleBlockState: StyleBlockStateType,
  assetState: AssetStateType
): string {
  const customLabel = memo_getUserCustomizedElementLabel(element);
  if (customLabel) return customLabel;

  return memo_getDefaultElementLabel(plugins)(styleBlockState)(assetState)(
    componentMap
  )(currentComponentRender)(element);
}

/*
  Returns a non-customized label for a given element based on various priority rules.

  Priority order:
  1. Component instance name
  2. Slot name (for slot elements)
  3. First style block name
  4. Text content (for typographic elements)
  5. Asset name (for image, link, and animation elements)
  6. Fallback to HTML tag or block element name
  7. Name directly from the element

  @param element - The expression element to get the label for
  @param currentComponentRender - The current component render expression
  @param componentMap - Map of components for component type checking
  @param plugins - Plugin system for the WFDL renderer
  @param styleBlockState - Style block state for retrieving style information
  @param assetState - Asset state for retrieving asset information
  @returns A string label representing the customized element name
 */
export function getDefaultElementLabel(
  element: ExpressionElement,
  currentComponentRender: Expression,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  styleBlockState: StyleBlockStateType,
  assetState: AssetStateType
): string {
  return memo_getDefaultElementLabel(plugins)(styleBlockState)(assetState)(
    componentMap
  )(currentComponentRender)(element);
}
